import { useRef, useEffect, useCallback, useState } from "react";
import utils from "../utils";
import { useStartupData } from "../modules";
import config from "../utils/config";

// 类型定义
type CallbackFunction = () => void | Promise<void>;
type DebounceFunction<T extends any[]> = (...args: T) => void;
type ThrottleFunction<T extends any[]> = (...args: T) => void;

/**
 * 自定义间隔 Hook
 * 支持异步回调函数和自定义延迟
 * @param callback 回调函数
 * @param span 间隔时间（毫秒）
 * @param delay 初始延迟时间（毫秒）
 */
export function useInterval(
  callback: CallbackFunction,
  span: number,
  delay: number = 0
) {
  const self = utils;
  const savedCallback = useRef<CallbackFunction>();
  const stopCallback = useRef({ stop: false });

  // 记住最新的回调函数
  useEffect(() => {
    savedCallback.current = callback;
  });

  const loop = async () => {
    if (delay) {
      await self.sleep(delay);
    }
    while (true) {
      if (stopCallback.current.stop) {
        break;
      }
      await savedCallback.current?.();
      await self.sleep(span || 1000);
    }
  };

  // 设置间隔
  useEffect(() => {
    const currentStopCallback = stopCallback.current;
    loop();
    return () => {
      currentStopCallback.stop = true;
    };
  }, [span, delay]);
}

// /**
//  * 防抖 Hook
//  * 在指定时间内只执行最后一次调用
//  * @param fn 要防抖的函数
//  * @param delay 延迟时间（毫秒）
//  * @param dep 依赖数组
//  * @returns 防抖后的函数
//  */
export function useDebounce<T extends any[]>(
  fn: DebounceFunction<T>,
  delay: number,
  dep: any[] = []
): DebounceFunction<T> {
  const { current } = useRef<{
    fn: DebounceFunction<T>;
    timer: NodeJS.Timeout | null;
  }>({ fn, timer: null });

  useEffect(
    function () {
      current.fn = fn;
    },
    [fn]
  );

  return useCallback(function f(...args: T) {
    if (current.timer) {
      clearTimeout(current.timer);
    }
    current.timer = setTimeout(() => {
      current.fn(...args);
    }, delay);
  }, dep);
}

/**
 * 节流 Hook
 * 在指定时间内只执行第一次调用
 * @param fn 要节流的函数
 * @param delay 延迟时间（毫秒）
 * @param dep 依赖数组
 * @returns 节流后的函数
 */
export function useThrottle<T extends any[]>(
  fn: ThrottleFunction<T>,
  delay: number,
  dep: any[] = []
): ThrottleFunction<T> {
  const { current } = useRef<{
    fn: ThrottleFunction<T>;
    timer: NodeJS.Timeout | null;
  }>({ fn, timer: null });

  useEffect(
    function () {
      current.fn = fn;
    },
    [fn]
  );

  return useCallback(function f(...args: T) {
    if (!current.timer) {
      current.timer = setTimeout(() => {
        current.timer = null;
      }, delay);
      current.fn(...args);
    }
  }, dep);
}

/**
 * 自定义定时器 Hook
 * 提供更灵活的定时器控制
 * @param callback 回调函数
 * @param interval 间隔时间（毫秒）
 * @param immediate 是否立即执行
 * @returns 控制对象
 */
export function useTimer(
  callback: CallbackFunction,
  interval: number,
  immediate: boolean = false
) {
  const savedCallback = useRef<CallbackFunction>();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef(false);

  // 记住最新的回调函数
  useEffect(() => {
    savedCallback.current = callback;
  });

  const start = useCallback(() => {
    if (isActiveRef.current) return;

    isActiveRef.current = true;

    if (immediate) {
      savedCallback.current?.();
    }

    timerRef.current = setInterval(() => {
      savedCallback.current?.();
    }, interval);
  }, [interval, immediate]);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    isActiveRef.current = false;
  }, []);

  const reset = useCallback(() => {
    stop();
    start();
  }, [start, stop]);

  // 清理定时器
  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  return {
    start,
    stop,
    reset,
    isActive: isActiveRef.current,
  };
}

/**
 * 倒计时 Hook
 * @param initialTime 初始时间（秒）
 * @param onComplete 完成回调
 * @returns 倒计时状态和控制函数
 */
export function useCountdown(initialTime: number, onComplete?: () => void) {
  const [timeLeft, setTimeLeft] = useState(initialTime);
  const [isActive, setIsActive] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const start = useCallback(() => {
    setIsActive(true);
  }, []);

  const stop = useCallback(() => {
    setIsActive(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    stop();
    setTimeLeft(initialTime);
  }, [stop, initialTime]);

  useEffect(() => {
    if (isActive && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsActive(false);
            onComplete?.();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isActive, timeLeft, onComplete]);

  return {
    timeLeft,
    isActive,
    start,
    stop,
    reset,
    isFinished: timeLeft === 0,
  };
}

/**
 * 结束时间倒计时时分秒
 * @param endAtMs 结束时间戳（毫秒）
 * @param onComplete 完成回调
 * @returns 倒计时状态和控制函数
 */
export function useCountdownTo(endAtMs: number, onComplete?: () => void) {
  const [timeLeft, setTimeLeft] = useState<number>(
    Math.max(0, Math.floor((endAtMs - Date.now()) / 1000))
  );
  const [isActive, setIsActive] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // endAtMs 变化时同步剩余时间
  useEffect(() => {
    setTimeLeft(Math.max(0, Math.floor((endAtMs - Date.now()) / 1000)));
  }, [endAtMs]);

  const tick = useCallback(() => {
    const next = Math.max(0, Math.floor((endAtMs - Date.now()) / 1000));
    setTimeLeft(next);
    if (next === 0) {
      setIsActive(false);
      onComplete?.();
    }
  }, [endAtMs, onComplete]);

  // 管理计时器生命周期
  useEffect(() => {
    if (isActive) {
      // 先对齐一次
      tick();
      timerRef.current = setInterval(tick, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isActive, tick]);

  const start = useCallback(() => setIsActive(true), []);
  const stop = useCallback(() => setIsActive(false), []);
  const reset = useCallback(() => {
    setIsActive(false);
    setTimeLeft(Math.max(0, Math.floor((endAtMs - Date.now()) / 1000)));
  }, [endAtMs]);

  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = timeLeft % 60;

  const pad = (n: number) => n.toString().padStart(2, "0");
  const hh = pad(hours);
  const mm = pad(minutes);
  const ss = pad(seconds);
  const hms = `${hh}:${mm}:${ss}`;

  const remainingMs = timeLeft * 1000;
  const isFinished = timeLeft === 0;

  return {
    // 数值
    timeLeft, // 剩余秒
    remainingMs, // 剩余毫秒
    endAt: endAtMs, // 结束时间戳（毫秒）

    // 分量与格式化
    hours,
    minutes,
    seconds,
    hh,
    mm,
    ss,
    hms,

    // 控制与状态
    isActive,
    isFinished,
    start,
    stop,
    reset,
  };
}

export function useCountryInfo() {
  const [countryId, setCountryId] = useState<string>(
    utils.getObject("countryId")
  );
  return { countryId, setCountryId };
}

export function useBetAmount() {
  const [betAmount, setBetAmount] = useState<number>(0);
  const { data: startupData } = useStartupData();
  useEffect(() => {
    setBetAmount(startupData?.defaultBetAmount || 0);
  }, [startupData?.defaultBetAmount]);
  return [betAmount || startupData?.defaultBetAmount, setBetAmount];
}
export function usePwaEvent() {
  const [event, setEvent] = useState<string | null>(null);
  const ref = useRef<((e: string) => void) | null>(null);

  // 设置事件处理器
  ref.current = (e: string) => {
    setEvent(e);
  };

  useEffect(() => {
    const handleAppInstalled = () => {
      ref.current?.("appinstalled");
    };

    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  return event;
}

export function useViewport() {
  const [width, setWidth] = useState(window.innerWidth);
  const forceMobileLayout = true;
  const handleWindowResize = useCallback(() => {
    setWidth(
      forceMobileLayout
        ? window.innerWidth <= 414
          ? window.innerWidth
          : 414
        : window.innerWidth
    );
  }, [forceMobileLayout]);

  useEffect(() => {
    handleWindowResize();
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [forceMobileLayout, handleWindowResize]);

  return { width };
}
export function useGameRule({
  gameId,
  setRuleShow,
}: {
  gameId: string;
  setRuleShow: (show: boolean) => void;
}) {
  useEffect(() => {
    const hasRuleShow = utils.getObject("hasRuleShow");
    if (!hasRuleShow || !hasRuleShow[gameId]) {
      setRuleShow(true);
    }
  }, [gameId]);
}

export function useCurrencySymbol(currencyId?: string | null) {
  const { data: startupData } = useStartupData();
  let currId = currencyId;
  if (!currencyId) {
    currId = utils.getObject("currencyId");
  }
  const clist = (startupData && startupData.currencies) || [];
  const c = clist.find(x => x.id === currId);
  return (c && c.symbol) || config.symbol;
}

export function useCurrencyLabel(currencyId?: string | null) {
  const { data: startupData } = useStartupData();
  let currId = currencyId;
  if (!currencyId) {
    currId = utils.getObject("currencyId");
  }
  const clist = (startupData && startupData.currencies) || [];
  const c = clist.find(x => x.id === currId);
  return (c && c.label) || "";
}
export function useCurrencyIcon(currencyId?: string | null) {
  const { data: startupData } = useStartupData();
  let currId = currencyId;
  if (!currencyId) {
    currId = utils.getObject("currencyId");
  }
  const clist = (startupData && startupData.currencies) || [];
  const c = clist.find(x => x.id === currId);
  return (c && c.icon) || "";
}
